import { useMemo, useState } from "react";
import { Link, useLocation } from "wouter";
import DashboardLayout from "@/components/DashboardLayout";
import { PermissionGuard } from "@/components/PermissionGuard";
import { trpc } from "@/lib/trpc";
import { usePermissions } from "@/hooks/usePermissions";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Trophy,
  Plus,
  Users,
  Target,
  TrendingUp,
  GitCompareArrows,
  Medal,
  Award,
  Crown,
  X,
} from "lucide-react";
import SellerProfileSheet from "@/components/Crm/SellerProfileSheet";

function formatBRL(raw: string | number | null | undefined): string {
  const n = typeof raw === "string" ? Number(raw) : raw ?? 0;
  if (Number.isNaN(n)) return "R$ 0,00";
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}
function formatCompact(raw: string | number | null | undefined): string {
  const n = typeof raw === "string" ? Number(raw) : raw ?? 0;
  if (Number.isNaN(n) || n === 0) return "R$ 0";
  if (Math.abs(n) >= 1_000_000) return `R$ ${(n / 1_000_000).toFixed(1).replace(".", ",")}M`;
  if (Math.abs(n) >= 1_000) return `R$ ${(n / 1_000).toFixed(1).replace(".", ",")}k`;
  return `R$ ${n.toFixed(0)}`;
}
function initials(name: string | null | undefined): string {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

type PeriodKind = "month" | "quarter" | "year";

function CrmSellersContent() {
  const [, navigate] = useLocation();
  const { can } = usePermissions();
  const canManage = can("crm:sellers_manage");

  const [kind, setKind] = useState<PeriodKind>("month");
  const [profileSheetOpen, setProfileSheetOpen] = useState(false);
  const [editingUserId, setEditingUserId] = useState<number | null>(null);
  const [compareIds, setCompareIds] = useState<number[]>([]);

  const rankingQuery = trpc.crm.sellers.ranking.useQuery({ kind });

  const rows = rankingQuery.data ?? [];

  const totals = useMemo(() => {
    let totalRevenue = 0;
    let totalWon = 0;
    let totalLost = 0;
    let totalGoal = 0;
    for (const r of rows) {
      totalRevenue += r.stats.wonRevenue;
      totalWon += r.stats.wonCount;
      totalLost += r.stats.lostCount;
      totalGoal += Number(r.profile.monthlyGoal ?? 0);
    }
    const winRate =
      totalWon + totalLost > 0 ? (totalWon / (totalWon + totalLost)) * 100 : null;
    return { totalRevenue, totalWon, totalLost, totalGoal, winRate };
  }, [rows]);

  function toggleCompare(userId: number) {
    setCompareIds((prev) => {
      if (prev.includes(userId)) return prev.filter((x) => x !== userId);
      if (prev.length >= 4) return prev;
      return [...prev, userId];
    });
  }

  const periodLabel = {
    month: "Este mês",
    quarter: "Este trimestre",
    year: "Este ano",
  }[kind];

  return (
    <div className="space-y-5">
      {/* ── Header ──────────────────────────────── */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
            <Trophy className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold tracking-tight truncate">
              Vendedores
            </h1>
            <p className="text-xs sm:text-sm text-muted-foreground hidden sm:block">
              Ranking, meta vs. realizado e comparação — {periodLabel.toLowerCase()}.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {compareIds.length >= 2 && (
            <Button
              variant="outline"
              onClick={() =>
                navigate(`/crm/sellers/compare?ids=${compareIds.join(",")}`)
              }
            >
              <GitCompareArrows className="h-4 w-4 sm:mr-1.5" />
              <span className="hidden sm:inline">Comparar ({compareIds.length})</span>
              <span className="sm:hidden ml-1.5">{compareIds.length}</span>
            </Button>
          )}
          {canManage && (
            <Button
              onClick={() => {
                setEditingUserId(null);
                setProfileSheetOpen(true);
              }}
            >
              <Plus className="h-4 w-4 sm:mr-1.5" />
              <span className="hidden sm:inline">Adicionar vendedor</span>
              <span className="sm:hidden ml-1.5">Novo</span>
            </Button>
          )}
        </div>
      </div>

      {/* ── Period picker ────────────────────── */}
      <div className="inline-flex items-center gap-1 p-1 bg-muted/60 rounded-lg">
        {(["month", "quarter", "year"] as PeriodKind[]).map((k) => (
          <button
            key={k}
            onClick={() => setKind(k)}
            className={`px-3 py-1 text-sm font-medium rounded-md transition ${
              kind === k
                ? "bg-background text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {k === "month" ? "Mês" : k === "quarter" ? "Trimestre" : "Ano"}
          </button>
        ))}
      </div>

      {/* ── KPIs globais ─────────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MetricCard
          label="Equipe"
          value={String(rows.length)}
          sub="vendedores ativos"
          icon={Users}
          tint="primary"
        />
        <MetricCard
          label="Receita ganha"
          value={formatCompact(totals.totalRevenue)}
          sub={`${totals.totalWon} deals`}
          icon={TrendingUp}
          tint="emerald"
        />
        <MetricCard
          label="Meta agregada"
          value={formatCompact(totals.totalGoal)}
          sub={
            totals.totalGoal > 0
              ? `${((totals.totalRevenue / totals.totalGoal) * 100).toFixed(0)}% atingido`
              : "sem meta definida"
          }
          icon={Target}
        />
        <MetricCard
          label="Win rate equipe"
          value={totals.winRate === null ? "—" : `${totals.winRate.toFixed(0)}%`}
          sub={totals.totalLost > 0 ? `${totals.totalLost} perdidos` : "—"}
          icon={Trophy}
          tint={totals.winRate !== null && totals.winRate >= 50 ? "emerald" : "default"}
        />
      </div>

      {/* ── Compare bar ───────────────────── */}
      {compareIds.length > 0 && (
        <Card className="bg-primary/5 border-primary/30">
          <CardContent className="p-3 flex items-center gap-2 flex-wrap">
            <GitCompareArrows className="h-4 w-4 text-primary shrink-0" />
            <span className="text-sm font-medium">
              Comparar {compareIds.length} vendedor{compareIds.length > 1 ? "es" : ""}
            </span>
            <span className="text-xs text-muted-foreground">(até 4)</span>
            <div className="flex-1" />
            <Button size="sm" variant="ghost" onClick={() => setCompareIds([])}>
              <X className="h-4 w-4 mr-1" /> Limpar
            </Button>
            {compareIds.length >= 2 && (
              <Button
                size="sm"
                onClick={() =>
                  navigate(`/crm/sellers/compare?ids=${compareIds.join(",")}`)
                }
              >
                Abrir comparador
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* ── Ranking ──────────────────────── */}
      {rankingQuery.isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-28 w-full" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <Trophy className="h-10 w-10 mx-auto mb-3 text-muted-foreground opacity-40" />
            <h3 className="font-semibold mb-1">Nenhum vendedor ainda</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Promova um usuário da equipe a vendedor com meta e comissão.
            </p>
            {canManage && (
              <Button
                onClick={() => {
                  setEditingUserId(null);
                  setProfileSheetOpen(true);
                }}
              >
                <Plus className="h-4 w-4 mr-1.5" /> Adicionar vendedor
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {rows.map((row: any, idx: number) => (
            <SellerRow
              key={row.profile.id}
              row={row}
              rank={idx + 1}
              canManage={canManage}
              selected={compareIds.includes(row.profile.userId)}
              onCompare={() => toggleCompare(row.profile.userId)}
              onEdit={() => {
                setEditingUserId(row.profile.userId);
                setProfileSheetOpen(true);
              }}
            />
          ))}
        </div>
      )}

      <SellerProfileSheet
        open={profileSheetOpen}
        onOpenChange={setProfileSheetOpen}
        userId={editingUserId}
      />
    </div>
  );
}

function SellerRow({
  row,
  rank,
  canManage,
  selected,
  onCompare,
  onEdit,
}: {
  row: any;
  rank: number;
  canManage: boolean;
  selected: boolean;
  onCompare: () => void;
  onEdit: () => void;
}) {
  const s = row.stats;
  const monthlyGoal = Number(row.profile.monthlyGoal ?? 0);
  const achievement = s.goalAchievement;
  const winRate = s.winRate;

  const rankBadge =
    rank === 1 ? (
      <div className="h-8 w-8 rounded-full bg-amber-100 dark:bg-amber-950 text-amber-700 dark:text-amber-300 flex items-center justify-center shrink-0">
        <Crown className="h-4 w-4" />
      </div>
    ) : rank === 2 ? (
      <div className="h-8 w-8 rounded-full bg-slate-100 dark:bg-slate-900 text-slate-600 dark:text-slate-300 flex items-center justify-center shrink-0">
        <Medal className="h-4 w-4" />
      </div>
    ) : rank === 3 ? (
      <div className="h-8 w-8 rounded-full bg-orange-100 dark:bg-orange-950 text-orange-700 dark:text-orange-300 flex items-center justify-center shrink-0">
        <Award className="h-4 w-4" />
      </div>
    ) : (
      <div className="h-8 w-8 rounded-full bg-muted text-muted-foreground flex items-center justify-center shrink-0 text-sm font-semibold">
        {rank}
      </div>
    );

  return (
    <div
      className={`group rounded-xl border bg-card p-4 transition hover:shadow-md ${
        selected ? "ring-2 ring-primary" : ""
      }`}
    >
      <div className="flex items-start gap-3 flex-wrap">
        {rankBadge}
        <Avatar className="h-12 w-12 shrink-0">
          <AvatarFallback className="bg-primary/10 text-primary font-semibold">
            {initials(row.user.name)}
          </AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <Link
              href={`/crm/sellers/${row.profile.userId}`}
              className="text-base font-semibold hover:text-primary truncate"
            >
              {row.user.name ?? row.user.email}
            </Link>
            {(row.profile.territories as string[])?.length > 0 && (
              <div className="flex gap-1">
                {(row.profile.territories as string[]).slice(0, 3).map((uf) => (
                  <Badge key={uf} variant="outline" className="text-[10px] h-4">
                    {uf}
                  </Badge>
                ))}
                {(row.profile.territories as string[]).length > 3 && (
                  <Badge variant="outline" className="text-[10px] h-4">
                    +{(row.profile.territories as string[]).length - 3}
                  </Badge>
                )}
              </div>
            )}
          </div>
          {row.user.jobTitle && (
            <p className="text-xs text-muted-foreground truncate">
              {row.user.jobTitle}
            </p>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <label
            className="flex items-center gap-1.5 px-2 py-1 text-xs cursor-pointer"
            onClick={(e) => e.stopPropagation()}
          >
            <Checkbox checked={selected} onCheckedChange={onCompare} />
            <span className="text-muted-foreground">Comparar</span>
          </label>
          {canManage && (
            <Button size="sm" variant="ghost" onClick={onEdit}>
              Editar
            </Button>
          )}
        </div>
      </div>

      {/* ── Progress meta ─────────────────── */}
      {monthlyGoal > 0 && (
        <div className="mt-3 space-y-1">
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">Meta vs. realizado</span>
            <span className="font-semibold tabular-nums">
              {formatCompact(s.wonRevenue)} / {formatCompact(monthlyGoal)}
              {achievement !== null && (
                <span
                  className={`ml-2 ${
                    achievement >= 100
                      ? "text-emerald-600"
                      : achievement >= 70
                      ? "text-amber-600"
                      : "text-muted-foreground"
                  }`}
                >
                  ({achievement.toFixed(0)}%)
                </span>
              )}
            </span>
          </div>
          <Progress
            value={Math.min(100, achievement ?? 0)}
            className={
              achievement !== null && achievement >= 100 ? "[&>div]:bg-emerald-500" : ""
            }
          />
        </div>
      )}

      {/* ── Stats row ───────────────────── */}
      <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 pt-3 border-t">
        <Stat label="Receita" value={formatBRL(s.wonRevenue)} />
        <Stat
          label="Fechados/Perdidos"
          value={`${s.wonCount} / ${s.lostCount}`}
          tint={winRate !== null && winRate >= 50 ? "emerald" : "default"}
        />
        <Stat
          label="Ticket médio"
          value={formatCompact(s.avgTicket)}
          sub={s.wonCount > 0 ? `${s.wonCount} deals` : undefined}
        />
        <Stat
          label="Ciclo médio"
          value={s.avgCycleDays === null ? "—" : `${s.avgCycleDays}d`}
          sub={winRate !== null ? `${winRate.toFixed(0)}% win rate` : undefined}
        />
      </div>
    </div>
  );
}

function Stat({
  label,
  value,
  sub,
  tint = "default",
}: {
  label: string;
  value: string;
  sub?: string;
  tint?: "default" | "emerald";
}) {
  return (
    <div>
      <div className="text-[10px] uppercase font-semibold tracking-wide text-muted-foreground">
        {label}
      </div>
      <div
        className={`text-sm font-bold tabular-nums truncate ${
          tint === "emerald" ? "text-emerald-600" : ""
        }`}
      >
        {value}
      </div>
      {sub && <div className="text-[10px] text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}

function MetricCard({
  label,
  value,
  sub,
  icon: Icon,
  tint = "default",
}: {
  label: string;
  value: string;
  sub?: string;
  icon: any;
  tint?: "default" | "emerald" | "red" | "primary";
}) {
  const tintMap = {
    default: "bg-muted text-muted-foreground",
    emerald: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300",
    red: "bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300",
    primary: "bg-primary/10 text-primary",
  };
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              {label}
            </div>
            <div className="mt-1 text-2xl font-bold tabular-nums truncate">{value}</div>
            {sub && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
          </div>
          <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${tintMap[tint]}`}>
            <Icon className="h-4 w-4" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function CrmSellers() {
  return (
    <DashboardLayout>
      <PermissionGuard permission="crm:sellers_view">
        <CrmSellersContent />
      </PermissionGuard>
    </DashboardLayout>
  );
}
